import React from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import type { AuditReportSummarySectionProps } from '../../../../types/auditReportDialogTypes';
import {
  formatAuditReportCurrency,
  formatAuditReportNumber,
} from '../utils/auditReportDialogUtils';

const AuditReportSummarySection: React.FC<AuditReportSummarySectionProps> = React.memo(({
  report,
  metrics,
}) => {
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {report.store_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Audit ID: {report.audit.audit_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Date: {metrics.formattedAuditDate}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          User: {report.audit.user_name}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Quantity Reconciliation
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Inventory Sold
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {formatAuditReportNumber(metrics.inventorySold)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Less POS Sold
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              -{formatAuditReportNumber(metrics.posSold)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Discrepancy
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: metrics.quantityDiscrepancy === 0 ? 'success.main' : 'error.main',
              }}
            >
              {formatAuditReportNumber(metrics.quantityDiscrepancy)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Revenue Reconciliation
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Inventory Sold Value
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {formatAuditReportCurrency(metrics.inventorySoldValue)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Less POS Sold Value
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              -{formatAuditReportCurrency(metrics.posSoldValue)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Less Free Tickets Given
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              -{formatAuditReportCurrency(metrics.freeTicketsGiven)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Discrepancy
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: metrics.revenueDiscrepancy === 0 ? 'success.main' : 'error.main',
              }}
            >
              {formatAuditReportCurrency(metrics.revenueDiscrepancy)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
});

AuditReportSummarySection.displayName = 'AuditReportSummarySection';

export default AuditReportSummarySection;
